// admin-panel/src/pages/Projects.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { projectsApi } from '../lib/api';
import { StatusBadge } from './Dashboard';
import {
  Plus, Search, Filter, Edit2, Trash2, Star, StarOff,
  Eye, Image, ChevronLeft, ChevronRight, MoreVertical
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function Projects() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ search: '', status: '', category: '', page: 1 });
  const [deleteId, setDeleteId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-projects', filters],
    queryFn: () => projectsApi.list({ ...filters, per_page: 12 }),
  });

  const { data: cats = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: projectsApi.categories,
  });

  const deleteMut = useMutation({
    mutationFn: projectsApi.delete,
    onSuccess: () => { qc.invalidateQueries(['admin-projects']); toast.success('Project deleted'); setDeleteId(null); },
    onError: () => toast.error('Failed to delete'),
  });

  const featuredMut = useMutation({
    mutationFn: projectsApi.toggleFeatured,
    onSuccess: () => { qc.invalidateQueries(['admin-projects']); toast.success('Updated'); },
  });

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Projects</h1>
          <p className="text-sm text-[#555] mt-0.5">Manage your portfolio projects</p>
        </div>
        <Link to="/projects/new" className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 transition-all">
          <Plus size={16} /> New Project
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <input value={filters.search} onChange={e => setFilter('search', e.target.value)}
            placeholder="Search projects..." className="w-full bg-[#151515] border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
        </div>
        {[
          { key: 'status', opts: ['', 'draft', 'published', 'archived'], label: 'Status' },
        ].map(({ key, opts, label }) => (
          <select key={key} value={filters[key]} onChange={e => setFilter(key, e.target.value)}
            className="bg-[#151515] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-[#888] focus:outline-none focus:border-[#D4AF37]/30 min-w-[130px]">
            <option value="">All {label}</option>
            {opts.filter(Boolean).map(o => <option key={o} value={o} className="bg-[#1A1A1A] capitalize">{o}</option>)}
          </select>
        ))}
        <select value={filters.category} onChange={e => setFilter('category', e.target.value)}
          className="bg-[#151515] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-[#888] focus:outline-none focus:border-[#D4AF37]/30 min-w-[150px]">
          <option value="">All Categories</option>
          {cats.map(c => <option key={c.id} value={c.id} className="bg-[#1A1A1A]">{c.name}</option>)}
        </select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array(8).fill(0).map((_, i) => <div key={i} className="aspect-[4/3] rounded-xl bg-white/5 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data?.data?.map(p => (
            <div key={p.id} className="group bg-[#151515] border border-white/5 rounded-xl overflow-hidden hover:border-[#D4AF37]/20 transition-all">
              {/* Thumbnail */}
              <div className="relative aspect-[4/3] overflow-hidden bg-[#1A1A1A]">
                {p.thumbnail || p.primary_image ? (
                  <img src={p.thumbnail || p.primary_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image size={28} className="text-[#333]" />
                  </div>
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Link to={`/projects/${p.id}/edit`} className="p-2 bg-white/10 hover:bg-[#D4AF37] rounded-lg transition-all"><Edit2 size={14} className="text-white" /></Link>
                  <button onClick={() => featuredMut.mutate(p.id)} className="p-2 bg-white/10 hover:bg-[#D4AF37]/20 rounded-lg transition-all">
                    {p.featured ? <Star size={14} className="text-[#D4AF37]" fill="#D4AF37" /> : <StarOff size={14} className="text-white" />}
                  </button>
                  <button onClick={() => setDeleteId(p.id)} className="p-2 bg-white/10 hover:bg-red-500/30 rounded-lg transition-all"><Trash2 size={14} className="text-white" /></button>
                </div>
                {/* Badges */}
                <div className="absolute top-2 left-2 flex gap-1">
                  <StatusBadge status={p.status} />
                </div>
                {p.featured && (
                  <div className="absolute top-2 right-2"><Star size={14} className="text-[#D4AF37]" fill="#D4AF37" /></div>
                )}
              </div>
              <div className="p-3">
                <p className="text-white text-sm font-medium truncate">{p.title}</p>
                <p className="text-[#555] text-xs mt-0.5">{p.category?.name || '—'} · {p.location || '—'}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !data?.data?.length && (
        <div className="text-center py-16 text-[#555]">
          <Image size={40} className="mx-auto mb-3 text-[#333]" />
          <p>No projects found.</p>
          <Link to="/projects/new" className="inline-flex items-center gap-2 mt-4 text-sm text-[#D4AF37] hover:underline"><Plus size={14} /> Add first project</Link>
        </div>
      )}

      {/* Pagination */}
      {data?.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button disabled={filters.page <= 1} onClick={() => setFilter('page', filters.page - 1)} className="p-2 rounded-lg bg-[#151515] border border-white/5 disabled:opacity-30 text-[#888] hover:text-white transition-all"><ChevronLeft size={16} /></button>
          <span className="text-sm text-[#555]">Page {filters.page} / {data.last_page}</span>
          <button disabled={filters.page >= data.last_page} onClick={() => setFilter('page', filters.page + 1)} className="p-2 rounded-lg bg-[#151515] border border-white/5 disabled:opacity-30 text-[#888] hover:text-white transition-all"><ChevronRight size={16} /></button>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Delete Project?</h3>
            <p className="text-[#888] text-sm mb-5">This will permanently delete the project and all its images/videos.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-xl text-sm text-[#888] bg-white/5 hover:bg-white/10 transition-all">Cancel</button>
              <button onClick={() => deleteMut.mutate(deleteId)} disabled={deleteMut.isPending} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-500/80 hover:bg-red-500 transition-all">
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
